import { EditorActions, useEditor } from '@candulabs/craft';
import { ButtonIcon, Callout, CollectionIcon, Tooltip } from 'canoe';
import flatten from 'lodash/flatten';
import React, { useRef, useState } from 'react';

import { CollectionSelector } from '@candu-ai/editor-nodes/collection-settings';
import { PopoverContainer } from 'craft/components/SelectedToolbar/Collections/components/StyledComponents';
import { NO_ACTION } from 'craft/constants';
import { useMultiNodes, useSelection } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';

export const CollectionContextMenu: React.FC = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const [isCollectionFormVisible, setCollectionFormVisible] = useState(true);
  const { selection } = useSelection();
  const { actions, query } = useEditor();
  const { viewportWrapperEl } = useViewportDom();
  const nodeIds = selection.all;

  const { props } = useMultiNodes(nodeIds);

  const {
    collectionId,
    maxRecords,
    actions: { setProps },
  } = useMultiNodes(nodeIds, (node) => ({
    collectionId: node?.data?.props?.collectionId,
    maxRecords: node?.data?.props?.maxRecords,
  }));

  const onChangeCollectionId = (oldCollectionId: string) => {
    // mutate descendant nodes consuming the collection
    actions.produceState((_, noTrackActions: EditorActions) => {
      if (!oldCollectionId) {
        return;
      }
      flatten(nodeIds.map((nodeId: string) => query.node(nodeId).descendants(true))).forEach(
        (childNodeId: string) => {
          const childCollections = query.node(childNodeId).node.data.custom.collections;
          if (!childCollections) {
            return;
          }
          const newCollections = childCollections.filter(
            (collection: any) => collection.collectionId !== oldCollectionId,
          );
          noTrackActions.setCustom(childNodeId, (custom: any) => {
            // eslint-disable-next-line no-param-reassign
            custom.collections = newCollections.length === 0 ? undefined : newCollections;
          });
        },
      );
    });
  };

  const toggleCollectionForm = () => setCollectionFormVisible(!isCollectionFormVisible);
  return (
    <>
      {isCollectionFormVisible && (
        <Callout
          referenceElement={ref.current}
          // eslint-disable-next-line react/prop-types
          onClose={() => props?.collectionId?.value && setCollectionFormVisible(false)}
          closeKey="Escape"
          placement="top"
          usePortal={false}
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
            >
              <PopoverContainer data-cy="popover-container">
                <CollectionSelector
                  context={{ isPopover: true }}
                  collectionId={collectionId}
                  maxRecords={maxRecords}
                  setProps={setProps}
                  onChangeCollectionId={onChangeCollectionId}
                />
              </PopoverContainer>
            </div>
          )}
        </Callout>
      )}
      <Tooltip placement="top" offset="xxs" content="Collection Settings">
        <ButtonIcon
          ref={ref}
          active={isCollectionFormVisible}
          onClick={() => window.requestAnimationFrame(toggleCollectionForm)}
          size="small"
          icon={CollectionIcon}
          radius="small"
          color="white"
          data-cy="field-selector-button"
        />
      </Tooltip>
    </>
  );
};
